///v1/organization/administrative/ticketingemail({organization_id})/

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { ODataService } from '../../../../../../../../core/services/odata.service';
import { environment } from '../../../../../../../../../environments/environment';
import { SKIP_ERROR_TOAST } from '../../../../../../../../core/constants/http-context-tokens';
import { getErrorBodyCode } from '../../../../../../../../core/models/api-error';
import { map } from 'rxjs';

/** 400 code returned when a request would add a second primary email to the org. */
export const PRIMARY_TICKETING_EMAIL_EXISTS = 'primary_ticketing_email_exists';

@Injectable({ providedIn: 'root' })
export class TicketingEmailService extends ODataService<TicketingEmailResponse> {
  constructor() {
    const url = `${environment.apiUrl}/v1/organization/administrative/ticketingemail`;
    super(inject(HttpClient), url);

    this.state = {
      skip: 0,
      take: 10,
      sort: [{ field: 'name', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };
  }

  public createEmail(request: TicketingEmailPostRequest) {
    const url = `${environment.apiUrl}/v1/organization/administrative/ticketingemail/`;
    return this.http.post<TicketingEmailPostRequest>(url, request, { context: this.writeContext() });
  }

  /** PATCH: only the fields present in the body are changed. */
  public updateEmail(email: TicketingEmailUpdateRequest) {
    const url = `${this.url}`;
    return this.http
      .patch<TicketingEmailResponse>(url, email, { context: this.writeContext() })
      .pipe(map((response) => response));
  }

  public deleteEmail(emailId: number) {
    const url = `${this.url}?pk=${emailId}`;
    return this.http.delete(url, { context: this.writeContext() }).pipe(map((response) => response));
  }

  /** Makes the email the only primary of the org; every other email becomes an alternate. */
  public makePrimary(emailId: number) {
    const url = `${this.url}make_primary/`;
    return this.http.post<void>(url, { pk: emailId }, { context: this.writeContext() });
  }

  // The component reports these errors itself (inline or with its own text),
  // so the interceptor's generic toast would only duplicate them.
  private writeContext(): HttpContext {
    return new HttpContext().set(
      SKIP_ERROR_TOAST,
      (status, body) =>
        status === 404 || (status === 400 && getErrorBodyCode(body) === PRIMARY_TICKETING_EMAIL_EXISTS)
    );
  }
}

export interface TicketingEmailResponse {
  pk: number;
  email: string;
  is_primary: boolean;
}

export interface TicketingEmailListResponse {
  '@odata.count': number;
  /** Primaries in the whole org, regardless of filters and paging. */
  primary_count: number;
  value: TicketingEmailResponse[];
}

export type TicketingEmailUpdateRequest = { pk: number } & Partial<Pick<TicketingEmailResponse, 'email' | 'is_primary'>>;

export interface TicketingEmailPostRequest {
  organization_id: number;
  ticketing_email: {
    pk?: string; // optional uuid4
    email: string;
    is_primary: boolean;
  };
}
